import { useCallback, useEffect, useState } from 'react';

const useSessionStorage = <T>(
  key: string,
  initialValue: T,
): [T, (value: T) => void] => {
  // Use useState to manage the state, initializing with a function to avoid SSR errors
  const [storedValue, _setStoredValue] = useState(() => {
    try {
      // Attempt to get the value from session storage, but only on the client side
      const item =
        typeof window !== 'undefined'
          ? window.sessionStorage.getItem(key)
          : null;
      // Parse the stored JSON string or return the initial value
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      // If error, return the initial value
      console.error(error);
      return initialValue;
    }
  });

  const setStoredValue = useCallback(
    (value: T) => {
      try {
        _setStoredValue(value);
        const valueToStore = JSON.stringify(value);
        if (typeof window !== 'undefined') {
          window.sessionStorage.setItem(key, valueToStore);
        }
      } catch (error) {
        console.error(error);
      }
    },
    [_setStoredValue, key],
  );

  // Use useEffect to handle updates to session storage
  useEffect(() => {
    try {
      // Serialize the current state value to a JSON string
      const valueToStore = JSON.stringify(storedValue);
      // Store the serialized value in session storage, but only on the client side
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(key, valueToStore);
      }
    } catch (error) {
      // If error, log it
      console.error(error);
    }
    // The effect should run any time the storedValue changes
  }, [storedValue, key]);

  // Return the state and a function to update it
  return [storedValue, setStoredValue];
};

export default useSessionStorage;
